/**
 * Product sync status line + "Crawl now" control. Shared by the brand
 * page and the products page. Reads the products query (count) + sync
 * summary (last run), and triggers an on-demand sync for managers.
 */

import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { useProducts, useSyncProducts } from "@/lib/queries";
import styles from "./ProductSyncBar.module.css";

export function ProductSyncBar({
  ws,
  brand,
  canManageBrands,
}: {
  ws: string;
  brand: string;
  canManageBrands: boolean;
}) {
  const productsQuery = useProducts(ws, brand);
  const sync = useSyncProducts(ws, brand);

  const data = productsQuery.data;
  const summary = data?.summary ?? null;
  const count = data?.products.length ?? 0;

  return (
    <div className={styles.bar}>
      <div className={styles.status}>
        {productsQuery.isLoading ? (
          <span className={styles.muted}>Loading products…</span>
        ) : summary ? (
          <span className={styles.line}>
            <strong>{count}</strong> product{count === 1 ? "" : "s"}
            {" · "}last synced {formatWhen(summary.lastCrawledAt)}
            {summary.failedCount > 0 && (
              <span className={styles.warn}> · {summary.failedCount} failed</span>
            )}
          </span>
        ) : (
          <span className={styles.muted}>Not synced yet.</span>
        )}
        {summary?.lastError && (
          <span className={styles.error}>
            <Icon name="alert" size={12} /> {summary.lastError}
          </span>
        )}
      </div>

      {canManageBrands && (
        <div className={styles.actions}>
          {sync.isSuccess && !sync.isPending && (
            <span className={styles.note}>Sync started — products appear shortly.</span>
          )}
          {sync.error && <span className={styles.error}>{sync.error.message}</span>}
          <Btn
            variant="secondary"
            size="sm"
            onClick={() => sync.mutate()}
            loading={sync.isPending}
            iconLeft={<Icon name="refresh" size={13} />}
          >
            {sync.isPending ? "Starting…" : "Crawl now"}
          </Btn>
        </div>
      )}
    </div>
  );
}

/** Compact relative time: "just now", "5 min ago", "3 hr ago", "2 days ago". */
function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "unknown";
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
